// frontend/src/components/public/blocks/OfferingsBlock.tsx
import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Button } from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';

interface OfferingsBlockProps {
  data: {
    title?: string;
    subtitle?: string;
    offerings: Array<{
      id: string;
      title: string;
      description: string;
      highlights: string[];
      pricingModel?: 'fixed' | 'tiered' | 'custom' | 'negotiable' | 'in_kind';
      terms?: string;
      deliverables?: string[];
      exclusivity?: boolean;
    }>;
    layout: 'cards' | 'list' | 'comparison';
  };
  hasContactForm?: boolean;
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const OfferingsBlock: React.FC<OfferingsBlockProps> = ({ data, hasContactForm, theme }) => {
  const { title, subtitle, offerings = [], layout = 'cards' } = data;
  const accentColor = theme?.accentColor || '#FF0000';
  const primaryColor = theme?.primaryColor || '#000000';

  if (offerings.length === 0) {
    return null;
  }

  const getPricingLabel = (model?: string) => {
    switch (model) {
      case 'fixed':
        return 'Fixed Price';
      case 'tiered':
        return 'Tiered Pricing';
      case 'custom':
        return 'Custom Package';
      case 'negotiable':
        return 'Negotiable';
      case 'in_kind':
        return 'In-Kind Support';
      default:
        return null;
    }
  };

  // Section Header Component (reused across layouts)
  const SectionHeader = () => (
    <>
      {(title || subtitle) && (
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          {title && (
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: primaryColor,
                mb: 2
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1rem', md: '1.25rem' },
                color: '#666666',
                fontWeight: 400
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
    </>
  );

  // Contact Button Component (reused across layouts)
  const ContactButton = () => {
    if (!hasContactForm) return null;

    return (
      <Button
        variant="contained"
        onClick={() => {
          const el = document.getElementById('contact');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        sx={{
          bgcolor: accentColor,
          color: 'white',
          mt: 3,
          alignSelf: 'center',
          px: 4,
          py: 1.5,
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 2,
          boxShadow: `0 4px 12px ${accentColor}44`,
          '&:hover': {
            bgcolor: accentColor,
            opacity: 0.9,
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${accentColor}55`
          },
          transition: 'all 0.3s ease'
        }}
      >
        Contact Us Now
      </Button>
    );
  };

  // Cards Layout
  if (layout === 'cards') {
    return (
      <Box
        id="offerings"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        <SectionHeader />
        <Grid container spacing={4}>
          {offerings.map((offering) => (
            <Grid
              item
              xs={12}
              md={offerings.length === 1 ? 12 : offerings.length === 2 ? 6 : 4}
              key={offering.id}
            >
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '2px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${accentColor}22`,
                    borderColor: accentColor
                  }
                }}
              >
                <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Header with badges */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        fontWeight: 700,
                        color: primaryColor,
                        mb: 2
                      }}
                    >
                      {offering.title}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {offering.exclusivity && (
                        <Chip
                          label="Exclusive"
                          size="small"
                          sx={{
                            bgcolor: accentColor,
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      )}
                      {offering.pricingModel && (
                        <Chip
                          label={getPricingLabel(offering.pricingModel)}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: primaryColor, color: primaryColor }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Description - NOW WITH RICH TEXT SUPPORT */}
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      fontSize: '1rem',
                      lineHeight: 1.7,
                      color: 'text.secondary',
                      mb: 3,
                      '& p': { mb: 1 },
                      '& strong': { fontWeight: 700, color: primaryColor },
                      '& em': { fontStyle: 'italic' },
                      '& a': {
                        color: accentColor,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      },
                      '& ul, & ol': { pl: 3, mb: 1 },
                      '& li': { mb: 0.5 }
                    }}
                    dangerouslySetInnerHTML={{ __html: offering.description }}
                  />

                  {/* Highlights */}
                  {offering.highlights.length > 0 && (
                    <Box sx={{ mb: 3, flex: 1 }}>
                      {(offering.highlights || []).map((highlight, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            mb: 1.5
                          }}
                        >
                          <CheckIcon
                            sx={{
                              color: accentColor,
                              fontSize: '1.25rem',
                              mt: 0.25,
                              flexShrink: 0
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.95rem',
                              lineHeight: 1.6,
                              color: 'text.primary'
                            }}
                          >
                            {highlight}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Terms */}
                  {offering.terms && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.875rem',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mt: 'auto'
                      }}
                    >
                      {offering.terms}
                    </Typography>
                  )}

                  {/* Contact Us Now Button */}
                  <ContactButton />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // List Layout
  if (layout === 'list') {
    return (
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1000px',
          mx: 'auto'
        }}
      >
        <SectionHeader />
        {offerings.map((offering, index) => (
          <Box
            key={offering.id}
            sx={{
              mb: 4,
              p: 4,
              bgcolor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  fontWeight: 700,
                  color: primaryColor,
                  mb: 2
                }}
              >
                {offering.title}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {offering.exclusivity && (
                  <Chip
                    label="Exclusive"
                    size="small"
                    sx={{
                      bgcolor: accentColor,
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
                {offering.pricingModel && (
                  <Chip
                    label={getPricingLabel(offering.pricingModel)}
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: primaryColor, color: primaryColor }}
                  />
                )}
              </Box>
            </Box>

            {/* Description - NOW WITH RICH TEXT SUPPORT */}
            <Typography
              variant="body1"
              component="div"
              sx={{
                fontSize: '1.05rem',
                lineHeight: 1.7,
                color: 'text.secondary',
                mb: 3,
                '& p': { mb: 1 },
                '& strong': { fontWeight: 700, color: primaryColor },
                '& em': { fontStyle: 'italic' },
                '& a': {
                  color: accentColor,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                },
                '& ul, & ol': { pl: 3, mb: 1 },
                '& li': { mb: 0.5 }
              }}
              dangerouslySetInnerHTML={{ __html: offering.description }}
            />

            {/* Highlights */}
            {offering.highlights.length > 0 && (
              <Box sx={{ mb: 3 }}>
                {offering.highlights.map((highlight, highlightIndex) => (
                  <Box
                    key={highlightIndex}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      mb: 1.5
                    }}
                  >
                    <CheckIcon
                      sx={{
                        color: accentColor,
                        fontSize: '1.25rem',
                        mt: 0.25,
                        flexShrink: 0
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '1rem',
                        lineHeight: 1.7,
                        color: 'text.primary'
                      }}
                    >
                      {highlight}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Terms */}
            {offering.terms && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.9rem',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  mt: 2
                }}
              >
                {offering.terms}
              </Typography>
            )}

            {/* Contact Us Now Button */}
            <ContactButton />
          </Box>
        ))}
      </Box>
    );
  }

  // Comparison Layout
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 3, md: 6 },
        maxWidth: '1400px',
        mx: 'auto'
      }}
    >
      <SectionHeader />
      <Grid container spacing={2}>
        {offerings.map((offering) => (
          <Grid item xs={12} md={12 / offerings.length} key={offering.id}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                border: '2px solid #e0e0e0',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: accentColor,
                  boxShadow: `0 8px 24px ${accentColor}22`
                }
              }}
            >
              <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 3, pb: 3, borderBottom: `2px solid ${accentColor}` }}>
                  <Typography
                    component="h3"
                    sx={{
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      fontWeight: 700,
                      color: primaryColor,
                      mb: 1,
                      lineHeight: 1.2
                    }}
                  >
                    {offering.title}
                  </Typography>

                  {offering.exclusivity && (
                    <Chip
                      label="Exclusive"
                      size="small"
                      sx={{
                        bgcolor: accentColor,
                        color: 'white',
                        fontWeight: 600,
                        mt: 1
                      }}
                    />
                  )}
                </Box>

                {/* Description - NOW WITH RICH TEXT SUPPORT */}
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    mb: 3,
                    textAlign: 'center',
                    '& p': { mb: 1 },
                    '& strong': { fontWeight: 700, color: primaryColor },
                    '& em': { fontStyle: 'italic' },
                    '& a': {
                      color: accentColor,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    },
                    '& ul, & ol': { pl: 3, mb: 1, textAlign: 'left' },
                    '& li': { mb: 0.5 }
                  }}
                  dangerouslySetInnerHTML={{ __html: offering.description }}
                />

                {/* Highlights as checklist */}
                <Box sx={{ mb: 3, flex: 1 }}>
                  {offering.highlights.map((highlight, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}
                    >
                      <CheckIcon
                        sx={{
                          color: accentColor,
                          fontSize: '1.1rem',
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.85rem',
                          lineHeight: 1.5,
                          color: 'text.primary'
                        }}
                      >
                        {highlight}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Terms */}
                {offering.terms && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      display: 'block',
                      textAlign: 'center',
                      mt: 3,
                      pt: 2,
                      borderTop: '1px solid #e0e0e0'
                    }}
                  >
                    {offering.terms}
                  </Typography>
                )}

                {/* Contact Us Now Button */}
                <ContactButton />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default OfferingsBlock;